import { useEffect, useMemo, useState } from 'react';

import type { ValueProposition } from '../../../../domain/preferences';
import { Preference } from '../../../../domain/preferences';
import { useValuePropPreferencesMutation } from '../../../../features/preferences/useValuePropPreferencesMutation';
import { useValuePropPreferencesQuery } from '../../../../features/preferences/useValuePropPreferencesQuery';
import { usePreferencesTracking } from '../usePreferencesTracking';

export const useValuePropositionPreferences = (onClose: () => void) => {
  const { handleTrackApplyPreferences, handleTrackResetValueProp } = usePreferencesTracking();
  const { data, isLoading } = useValuePropPreferencesQuery();
  const { custom, default: defaultValue } = data || {};
  const [painPoint, setPainPoint] = useState(custom?.painPoint || '');
  const [valueProposition, setValueProposition] = useState(custom?.valueProposition || '');

  const { mutate, isPending: isMutationLoading } = useValuePropPreferencesMutation(onClose);

  const setValues = (value?: ValueProposition) => {
    setPainPoint(value?.painPoint || '');
    setValueProposition(value?.valueProposition || '');
  };

  useEffect(() => {
    if (custom) {
      setValues(custom);
    }
  }, [custom]);

  const onReset = () => {
    handleTrackResetValueProp();
    setValues(defaultValue);
  };

  const onSave = () => {
    handleTrackApplyPreferences({ selectedPreference: Preference.valueProposition });
    mutate({ painPoint, valueProposition });
  };

  const isNoChangeInValues = useMemo(
    () => painPoint === custom?.painPoint && valueProposition === custom?.valueProposition,
    [painPoint, valueProposition, custom?.painPoint, custom?.valueProposition]
  );

  const isSaveDisabled = useMemo(
    () => isLoading || painPoint.length === 0 || valueProposition.length === 0 || isNoChangeInValues,
    [isLoading, painPoint, valueProposition, isNoChangeInValues]
  );

  const isResetDisabled = useMemo(
    () => isLoading || (painPoint === defaultValue?.painPoint && valueProposition === defaultValue?.valueProposition),
    [isLoading, painPoint, valueProposition, defaultValue?.painPoint, defaultValue?.valueProposition]
  );

  const isResetAvailable = useMemo(
    () => !!defaultValue?.painPoint && !!defaultValue.valueProposition,
    [defaultValue?.painPoint, defaultValue?.valueProposition]
  );

  return {
    painPoint,
    setPainPoint,
    valueProposition,
    setValueProposition,
    isSaveDisabled,
    isNoChangeInValues,
    isResetDisabled,
    isResetAvailable,
    isLoading,
    isMutationLoading,
    onReset,
    onSave,
  };
};
